import api, { apiErrorMessage } from './api';
import { downloadFromApi } from './download';

/**
 * Appels des rapports mensuels de la flotte (lot 020).
 *
 * Préfixe `/fleet-reports`, réservé à la direction (`canSeeMoney`). Les quatre
 * états acceptent `format=json|pdf|xlsx|csv`; hors `json`, la réponse est un
 * fichier dont le nom est donné par le serveur (`downloadFromApi`).
 *
 * Ce module ne contient aucun libellé d'écran: les textes lisibles viennent
 * déjà en français de la réponse.
 */

const BASE = '/fleet-reports';

/** Clé d'état → chemin de l'API. */
export const CHEMINS_ETATS = {
  compte: 'operating-account',
  synthese: 'truck-summary',
  loues: 'rented-trucks',
  bilan: 'monthly-balance',
};

/** Format de fichier → [extension, type MIME] pour le nom de repli du téléchargement. */
export const FORMATS_FICHIERS = {
  pdf: ['pdf', 'application/pdf'],
  xlsx: ['xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  csv: ['csv', 'text/csv'],
};

const NOMS_DE_REPLI = {
  compte: 'compte-exploitation',
  synthese: 'synthese-par-camion',
  loues: 'camions-loues',
  bilan: 'bilan-mensuel',
};

const deuxChiffres = (n) => String(n).padStart(2, '0');

/** Paramètres de requête d'un état: les filtres vides ne partent pas. */
export const parametresEtat = (etat, { annee, mois, camionId, transporteurId } = {}) => {
  const params = { year: annee, month: mois };
  if (etat === 'compte') {
    if (camionId) params.truck_id = camionId;
    if (transporteurId) params.company_id = transporteurId;
  }
  return params;
};

const lireJson = async (promesse) => {
  const reponse = await promesse;
  return reponse.data;
};

export const fleetReportsAPI = {
  /** Lit un des quatre états au format JSON. */
  lire(etat, options = {}) {
    const chemin = CHEMINS_ETATS[etat];
    if (!chemin) throw new Error('État inconnu');
    return lireJson(api.get(`${BASE}/${chemin}`, { params: parametresEtat(etat, options) }));
  },

  compteExploitation: (options) => fleetReportsAPI.lire('compte', options),
  syntheseParCamion: (options) => fleetReportsAPI.lire('synthese', options),
  camionsLoues: (options) => fleetReportsAPI.lire('loues', options),
  bilanMensuel: (options) => fleetReportsAPI.lire('bilan', options),

  /** Instantané du mois et nombre de saisies faites après la clôture. */
  statutCloture({ annee, mois }) {
    return lireJson(api.get(`${BASE}/monthly-close`, { params: { year: annee, month: mois } }));
  },

  /** Clôture le mois (idempotent: une seconde demande rend l'instantané existant). */
  cloturer({ annee, mois }) {
    return lireJson(api.post(`${BASE}/monthly-close`, { year: annee, month: mois }));
  },

  /** Télécharge l'état au format voulu (`pdf`, `xlsx` ou `csv`). */
  telecharger(etat, format, options = {}) {
    const chemin = CHEMINS_ETATS[etat];
    const fichier = FORMATS_FICHIERS[format];
    if (!chemin || !fichier) throw new Error('Format inconnu');
    const [extension, type] = fichier;
    const { annee, mois } = options;
    const repli = `${NOMS_DE_REPLI[etat]}-${annee}-${deuxChiffres(mois)}.${extension}`;
    return downloadFromApi(
      `${BASE}/${chemin}`,
      { ...parametresEtat(etat, options), format },
      repli,
      type,
    );
  },
};

const MESSAGES_TECHNIQUES = /^(request failed|network error|timeout of|status code|aborted)/i;

/** Lit le message français d'une réponse d'erreur portée par un fichier (`Blob`). */
const messageDuBlob = async (donnees) => {
  if (!donnees || typeof donnees.text !== 'function') return null;
  try {
    const corps = JSON.parse(await donnees.text());
    return corps?.message || corps?.error?.message || corps?.detail || null;
  } catch {
    return null;
  }
};

/**
 * Message d'erreur à montrer à l'opérateur: celui du serveur (en français),
 * jamais « Request failed with status code 403 ». Une erreur de téléchargement
 * arrive sous forme de fichier: on en lit le contenu.
 */
export const messageErreurRapport = async (error, repli = 'Une erreur est survenue.') => {
  const data = error?.response?.data;
  const duBlob = await messageDuBlob(data);
  if (duBlob) return duBlob;
  const message = apiErrorMessage(error, '');
  if (!message || MESSAGES_TECHNIQUES.test(message)) {
    return error?.response ? repli : 'Rapport indisponible pour le moment. Réessayez dans un instant.';
  }
  return message;
};

export default fleetReportsAPI;
